import { useState } from "react";
import { Check } from "lucide-react";

import Avatar from "./Avatar";
import { AVATARS, DEFAULT_AVATAR_ID } from "../utils/avatars";
import { USERNAME_MAX, validateUsername } from "../hooks/useProfile";
import { SHADOW, TOKENS as T, inputStyle } from "../styles/styles";

export default function OnboardingScreen({ user, onSave }) {
  const suggested = (user?.email || "").split("@")[0].slice(0, USERNAME_MAX);

  const [name, setName] = useState(suggested);
  const [avatarId, setAvatarId] = useState(DEFAULT_AVATAR_ID);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    const problem = validateUsername(name);

    if (problem) {
      setError(problem);
      return;
    }

    try {
      setError("");
      setSaving(true);
      await onSave({ displayName: name, avatarId });
    } catch (saveError) {
      console.error("Could not save profile:", saveError);
      setError("That didn't save. Give it another go.");
      setSaving(false);
    }
  }

  return (
    <div style={pageStyle}>
      <div style={cardStyle}>
        <div style={previewStyle}>
          <Avatar avatarId={avatarId} size={80} />
        </div>

        <h1 style={titleStyle}>Nice to meet you</h1>
        <p style={subtitleStyle}>
          Pick a name and a look. This is how your group will see you.
        </p>

        <p style={sectionLabelStyle}>Your name</p>
        <input
          value={name}
          onChange={(event) => setName(event.target.value)}
          onKeyDown={(event) => event.key === "Enter" && handleSave()}
          placeholder="e.g. Affana"
          maxLength={USERNAME_MAX}
          style={inputStyle}
        />

        <p style={{ ...sectionLabelStyle, marginTop: 20 }}>Your avatar</p>
        <div style={galleryStyle}>
          {AVATARS.map((avatar) => {
            const selected = avatar.id === avatarId;

            return (
              <button
                key={avatar.id}
                type="button"
                onClick={() => setAvatarId(avatar.id)}
                aria-label={avatar.name}
                style={{
                  ...avatarButtonStyle,
                  border: selected
                    ? `2px solid ${T.primary}`
                    : `2px solid transparent`,
                  background: selected ? "rgba(0,119,255,0.06)" : "transparent",
                }}
              >
                <Avatar avatarId={avatar.id} size={52} />

                {selected && (
                  <span style={checkStyle}>
                    <Check size={11} color="#fff" strokeWidth={3} />
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {error && <p style={errorStyle}>{error}</p>}

        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          style={{ ...submitStyle, opacity: saving ? 0.6 : 1 }}
        >
          {saving ? "Saving…" : "Start tracking"}
        </button>
      </div>
    </div>
  );
}

const pageStyle = {
  minHeight: "100vh",
  background: T.background,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "24px 20px",
};

const cardStyle = {
  width: "100%",
  maxWidth: 420,
  background: T.card,
  borderRadius: 24,
  padding: 24,
  boxShadow: SHADOW.card,
};

const previewStyle = {
  display: "flex",
  justifyContent: "center",
  marginBottom: 16,
};

const titleStyle = {
  margin: 0,
  fontSize: 24,
  fontWeight: 800,
  textAlign: "center",
  color: T.foreground,
};

const subtitleStyle = {
  margin: "6px 0 24px",
  fontSize: 13,
  lineHeight: 1.5,
  textAlign: "center",
  color: T.mutedForeground,
};

const sectionLabelStyle = {
  margin: "0 0 8px",
  fontSize: 10,
  fontWeight: 600,
  color: T.mutedForeground,
  textTransform: "uppercase",
  letterSpacing: "0.1em",
};

const galleryStyle = {
  display: "flex",
  gap: 8,
  justifyContent: "space-between",
};

const avatarButtonStyle = {
  position: "relative",
  padding: 4,
  borderRadius: 20,
  cursor: "pointer",
  lineHeight: 0,
  minHeight: 60,
};

const checkStyle = {
  position: "absolute",
  right: 2,
  bottom: 2,
  width: 18,
  height: 18,
  borderRadius: "50%",
  background: T.primary,
  border: `2px solid ${T.card}`,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const errorStyle = {
  margin: "16px 0 0",
  fontSize: 13,
  fontWeight: 600,
  color: T.destructive,
};

const submitStyle = {
  marginTop: 24,
  width: "100%",
  minHeight: 48,
  padding: "14px 16px",
  borderRadius: 16,
  border: "none",
  background: T.primary,
  color: T.primaryForeground,
  fontSize: 14,
  fontWeight: 700,
  fontFamily: "inherit",
  cursor: "pointer",
};
